import { useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Select } from "@legal-500/legal500-design-system";
import { fetchAllEditionBpas, researchCms } from "@/api/researchCms.service";
import type { EditionBpa } from "@/api/researchCms.service";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { CmsConfirmDialog } from "../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import { messageOf } from "./components/BpaImport/importText";
import { isEditionLocked } from "./utils/bpaImportEditionStatus";
import { ALL_KINDS, firmKey, type ImportPreviewOrg } from "./utils/bpaImport";
import {
  archivePlan,
  hubPermissions,
  pickerCandidates,
  previewAllowed,
  previewTargetId as choosePreviewTargetId,
  readSplitCardsFromUrl,
  SPLIT_STILL_IN_FAILED_REASON,
  splitHubModel,
  splitHubUrl,
  splitImportUrl,
  stillInFromSummary,
  stillInUnavailableText,
  stillInView,
  type SplitCard,
  type SplitCardInput,
  type StillInRow,
} from "./utils/bpaSplit";
import hub from "./CmsBpaHub.module.css";
import styles from "./CmsBpaSplitHubPage.module.css";
import shared from "./components/BpaImport/importShared.module.css";

const ARCHIVE_REASON_ID = "split-archive-reason";
const VIEW_ONLY_REASON = "You have view access only.";
const NO_ROWS: StillInRow[] = [];
const NO_CARDS: SplitCardInput[] = [];
const PICKER_LOADING_LABEL = "Loading practice areas...";
const NAMES_UNAVAILABLE_TEXT = "Names couldn't be loaded";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB");
}

function movedLines(card: SplitCard): { headline: string | null; detail: string } {
  if (!card.moved) return { headline: null, detail: "Nothing moved yet" };
  const date = card.movedAt ? formatDate(card.movedAt) : null;
  if (!card.movedAnything) {
    return { headline: null, detail: ["Nothing moved", date].filter(Boolean).join(" · ") };
  }
  const count =
    card.movedCount > 0
      ? `${card.movedCount} ${card.movedCount === 1 ? "organisation" : "organisations"} moved`
      : "Content moved in";
  const names = card.movedNamesUnavailable ? NAMES_UNAVAILABLE_TEXT : card.movedNames;
  return { headline: count, detail: [names, date].filter(Boolean).join(" · ") };
}

function toInput(card: SplitCard): SplitCardInput {
  return { editionBpaId: card.editionBpaId, bpaId: card.bpaId, name: card.name };
}

function enrich(card: SplitCardInput, editionBpas: EditionBpa[]): SplitCardInput {
  const known = editionBpas.find(bpa => bpa.id === card.editionBpaId);
  if (!known) return card;
  return { editionBpaId: card.editionBpaId, bpaId: known.bpaId, name: known.bpaName };
}

function hasContent(org: ImportPreviewOrg): boolean {
  return Object.values(org.sourceCounts).some(count => count > 0);
}

function isInMerge(bpa: EditionBpa): boolean {
  return (bpa.mergeTargetEditionBpaId ?? null) !== null || (bpa.mergeSourceCount ?? 0) > 0;
}

type FinishInput = { originalEditionBpaId: string; newEditionBpaIds: string[] };

export function CmsBpaSplitHubPage() {
  const { editionId = "", bpaId = "" } = useParams<{ editionId: string; bpaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const [showAll, setShowAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [checking, setChecking] = useState(false);
  const archiveButtonRef = useRef<HTMLButtonElement>(null);

  const flagOn = hasFlag("research-cms-1-enabled");
  const canRead = hasPermission("staff.research.read");
  const canEdit = hasPermission("staff.research.edit");
  const canDelete = hasPermission("staff.research.delete");
  const gateOpen = flagOn && canRead;

  const baseQuery = useQuery({
    queryKey: ["split-hub-base", editionId],
    enabled: gateOpen,
    queryFn: async () => {
      const [editionBpas, editions] = await Promise.all([
        fetchAllEditionBpas({ editionId, includeArchived: true }),
        researchCms.getEditions({ editionId, limit: 1 }),
      ]);
      const edition = editions.editionConfigs[0];
      return {
        editionBpas,
        editionName: edition?.editionName ?? "",
        editionStatus: edition?.status ?? null,
      };
    },
  });

  const editionBpas = baseQuery.data?.editionBpas;
  const original = editionBpas?.find(bpa => bpa.bpaId === bpaId) ?? null;
  const originalName = original?.bpaName ?? "";
  const editionStatus = baseQuery.data?.editionStatus ?? null;

  const historyQuery = useQuery({
    queryKey: ["import-history", original?.id],
    enabled: gateOpen && !!original,
    queryFn: () => researchCms.getImportHistory(original!.id),
  });
  const history = historyQuery.data?.importHistory;
  const firmNamesUnavailable = historyQuery.data?.firmNamesUnavailable ?? false;
  const originalArchived = original?.status === "archived";

  const cardsFromUrl = readSplitCardsFromUrl(searchParams).map(card =>
    enrich(card, editionBpas ?? []),
  );
  const cardInputs =
    original && history
      ? splitHubModel({
          originalEditionBpaId: original.id,
          history,
          cardsFromUrl,
          stillIn: NO_ROWS,
        }).cards.map(toInput)
      : NO_CARDS;

  const previewTargetId = choosePreviewTargetId({
    cards: cardInputs,
    practiceAreas: (editionBpas ?? []).map(bpa => ({ id: bpa.id, status: bpa.status })),
    originalEditionBpaId: original?.id ?? "",
  });
  const canPreview = !!original && previewAllowed({ editionStatus, originalArchived });

  const stillInQuery = useQuery({
    queryKey: ["split-hub-still-in", original?.id, previewTargetId],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: gateOpen && canPreview && !!history && !!previewTargetId,
    queryFn: async () => {
      const preview = await researchCms.getImportPreview(previewTargetId!, {
        sourceEditionBpaId: original!.id,
        contentTypes: ALL_KINDS,
        excludeCompanyIds: [],
        excludeNoFirm: false,
        clashResolution: {},
      });
      return preview.orgs.filter(hasContent).map(
        (org): StillInRow => ({
          key: firmKey(org),
          name: org.noFirm ? "No firm" : (org.companyName ?? "Unnamed organisation"),
          noFirm: org.noFirm,
          counts: org.sourceCounts,
        }),
      );
    },
  });

  const summaryEnabled =
    gateOpen && !!original && !!history && isEditionLocked(editionStatus) && !originalArchived;
  const summaryQuery = useQuery({
    queryKey: ["split-hub-summary", original?.id],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: summaryEnabled,
    queryFn: async () =>
      stillInFromSummary((await researchCms.getContentSummary(original!.id)).firms),
  });

  const needsPicker = cardInputs.some(card => !card.editionBpaId);
  const overviewQuery = useQuery({
    queryKey: ["split-hub-overview", editionId],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: gateOpen && !!original && needsPicker,
    queryFn: () => researchCms.getImportOverview(editionId),
  });

  const archive = useMutation({
    mutationFn: (input: FinishInput) =>
      researchCms.finishSplit(input.originalEditionBpaId, input.newEditionBpaIds),
    onSuccess: () => {
      navigate(ROUTES.research.cmsBpa(editionId, bpaId));
      for (const key of [
        "research-edition-bpas",
        "research-bpa-stats",
        "research-bpa-detail",
        "import-history",
        "split-hub-base",
        "split-hub-still-in",
        "split-hub-summary",
        "split-hub-overview",
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["split-hub-base"] });
      queryClient.invalidateQueries({ queryKey: ["split-hub-still-in"] });
      queryClient.invalidateQueries({ queryKey: ["split-hub-summary"] });
      queryClient.invalidateQueries({ queryKey: ["import-history"] });
    },
  });

  if (flagsLoading) return null;

  const permissions = hubPermissions({ flagOn, canRead, canEdit, canDelete, editionStatus });
  if (!permissions.open) return <UnauthorizedView />;

  const previewFailed = canPreview && stillInQuery.isError;
  const failed = baseQuery.isError || historyQuery.isError || previewFailed;
  const loadError =
    baseQuery.error ?? historyQuery.error ?? (previewFailed ? stillInQuery.error : null);
  const retry = async () => {
    const base = await baseQuery.refetch();
    if (base.isError || !base.data) return;
    if (historyQuery.isError) historyQuery.refetch();
    const nowArchived =
      base.data.editionBpas.find(bpa => bpa.bpaId === bpaId)?.status === "archived";
    const previewStillAllowed = previewAllowed({
      editionStatus: base.data.editionStatus,
      originalArchived: nowArchived,
    });
    if (stillInQuery.isError && previewStillAllowed) stillInQuery.refetch();
  };

  const breadcrumbs = [
    {
      label: baseQuery.data?.editionName || "Edition",
      path: ROUTES.research.cmsPublication(editionId),
    },
    ...(original?.rankingEntityName
      ? [
          {
            label: original.rankingEntityName,
            path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${original.rankingEntityId}`,
          },
        ]
      : []),
    { label: originalName || "Practice area", path: ROUTES.research.cmsBpa(editionId, bpaId) },
    { label: "Split" },
  ];

  if (failed) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <div className={hub.error} role="alert">
          <span>{messageOf(loadError)}</span>
          <Button variant="outlined" color="default" onClick={retry}>
            Retry
          </Button>
        </div>
      </CmsLayout>
    );
  }

  const stillIn = summaryEnabled ? summaryQuery.data : stillInQuery.data;
  if (baseQuery.data && !original) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <p className={hub.status}>This practice area is not in this edition.</p>
        <Link to={ROUTES.research.cmsPublication(editionId)}>Back to the edition</Link>
      </CmsLayout>
    );
  }

  const ready = !!original && !!history && !!baseQuery.data;
  const waitingForPreview = ready && canPreview && !!previewTargetId && stillIn === undefined;
  const waitingForSummary = ready && summaryEnabled && summaryQuery.isPending;

  if (!ready || waitingForPreview || waitingForSummary) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <p className={hub.status} role="status">
          Loading...
        </p>
      </CmsLayout>
    );
  }

  const stillInKnown = stillIn !== undefined;
  const rows = stillIn ?? NO_ROWS;
  const model = splitHubModel({
    originalEditionBpaId: original.id,
    history,
    cardsFromUrl,
    stillIn: stillIn ?? null,
    originalArchived,
    archivedEditionBpaIds: (editionBpas ?? [])
      .filter(bpa => bpa.status === "archived")
      .map(bpa => bpa.id),
    firmNamesUnavailable,
  });
  const cards = model.cards.map(card => {
    const known = editionBpas?.find(bpa => bpa.id === card.editionBpaId);
    return {
      ...card,
      bpaId: known?.bpaId ?? card.bpaId,
      name: known?.bpaName ?? card.name,
    };
  });
  const cardInputsNow = cards.map(toInput);
  const view = stillInView(rows, showAll, originalName);
  const archivedEditionBpaIds = (editionBpas ?? [])
    .filter(bpa => bpa.status === "archived")
    .map(bpa => bpa.id);
  const plan = archivePlan({
    originalName,
    stillIn: stillIn ?? null,
    cards,
    archivedEditionBpaIds,
  });

  const summaryFailed = summaryEnabled && summaryQuery.isError;
  const archiveReason =
    (permissions.viewOnly ? VIEW_ONLY_REASON : null) ??
    permissions.archiveDisabledReason ??
    plan.blockedReason ??
    (summaryFailed ? SPLIT_STILL_IN_FAILED_REASON(originalName) : null);
  const moveInReason = permissions.viewOnly ? VIEW_ONLY_REASON : permissions.moveInDisabledReason;
  const archiveOffered = model.archiveOffered;

  const overviewHasContent = new Map(
    (overviewQuery.data?.editionBpas ?? []).map(item => [item.editionBpaId, item.hasContent]),
  );
  const overviewLoading = overviewQuery.isLoading;
  const candidates = pickerCandidates(
    (editionBpas ?? []).map(bpa => ({
      editionBpaId: bpa.id,
      bpaId: bpa.bpaId,
      name: bpa.bpaName,
      status: bpa.status,
      hasContent: overviewHasContent.get(bpa.id) ?? true,
      inMerge: isInMerge(bpa),
    })),
    original.id,
    null,
  );

  function choose(slot: number, editionBpaId: string) {
    const picked = candidates.find(item => item.editionBpaId === editionBpaId);
    if (!picked) return;
    const next = cardInputsNow.map((card, index) =>
      index === slot ? { editionBpaId, bpaId: picked.bpaId, name: picked.name } : card,
    );
    setSearchParams(new URLSearchParams(splitHubUrl(editionId, bpaId, next).split("?")[1] ?? ""), {
      replace: true,
    });
  }

  function openMoveIn(targetBpaId: string | null) {
    if (!targetBpaId) return;
    navigate(
      splitImportUrl({
        targetEditionId: editionId,
        targetBpaId,
        sourceEditionBpaId: original!.id,
        sourceEditionId: editionId,
        originalEditionId: editionId,
        originalBpaId: bpaId,
        cards: cardInputsNow,
      }),
    );
  }

  async function stillInRefreshed() {
    if (summaryEnabled && (await summaryQuery.refetch()).isError) return false;
    if (canPreview && previewTargetId && (await stillInQuery.refetch()).isError) return false;
    return true;
  }

  async function requestArchive() {
    setChecking(true);
    try {
      if (await stillInRefreshed()) setConfirming(true);
    } finally {
      setChecking(false);
    }
  }

  function archiveOriginal() {
    setConfirming(false);
    archive.mutate({
      originalEditionBpaId: original!.id,
      newEditionBpaIds: plan.newEditionBpaIds,
    });
  }

  const archiving = archive.isPending;
  const archiveError = archive.isError ? messageOf(archive.error) : null;

  const archiveButton = archiveOffered && (
    <Button
      ref={archiveButtonRef}
      className={shared.compactButton}
      variant={model.archiveIsMain ? "contained" : "outlined"}
      color={model.archiveIsMain ? "primary" : "default"}
      disabled={archiving || checking || archiveReason !== null}
      title={archiveReason ?? undefined}
      aria-describedby={archiveReason ? ARCHIVE_REASON_ID : undefined}
      onClick={requestArchive}
    >
      {archiving ? "Archiving..." : "Archive original"}
    </Button>
  );

  const confirmation = plan.confirmation;
  const warning = confirmation[confirmation.length - 1];
  const details = confirmation.slice(0, -1);

  return (
    <CmsLayout breadcrumbs={breadcrumbs}>
      <div className={hub.page}>
        <div className={hub.column}>
          <div className={hub.heading}>
            <h1 className={hub.title}>Split {originalName}</h1>
            <p className={hub.meta}>
              {[baseQuery.data?.editionName, original?.rankingEntityName]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <p className={hub.explainer}>
              {`Move content from ${originalName} into each practice area below. When both are done, archive the original to finish the split.`}
            </p>
          </div>

          <section className={hub.box} aria-labelledby="split-cards-heading">
            <div className={hub.boxHeader}>
              <h2 id="split-cards-heading" className={hub.micro}>
                Splitting into
              </h2>
              <span className={hub.count}>{cards.length}</span>
            </div>
            <div className={hub.cards}>
              {cards.map((card, slot) => {
                const lines = card.needsChoosing
                  ? { headline: null, detail: "Nothing moved yet" }
                  : movedLines(card);
                return (
                  <div key={card.editionBpaId ?? `slot-${slot}`} className={hub.card}>
                    <div className={hub.cardName} data-pending={card.needsChoosing || undefined}>
                      {card.moved && !card.needsChoosing && (
                        <span className={hub.doneMark} aria-hidden="true">
                          ✓
                        </span>
                      )}
                      {card.needsChoosing ? "Choose a practice area" : card.name}
                    </div>
                    {lines.headline && <p className={hub.cardLine}>{lines.headline}</p>}
                    <div className={hub.muted}>{lines.detail}</div>
                    {card.archived && <span className={styles.archivedTag}>Archived</span>}
                    <div className={hub.cardAction}>
                      {card.needsChoosing && (
                        <div className={hub.cardPicker}>
                          <Select
                            aria-label={`Choose a practice area for half ${slot + 1}`}
                            fullWidth
                            value=""
                            disabled={permissions.viewOnly || overviewLoading}
                            title={permissions.viewOnly ? VIEW_ONLY_REASON : undefined}
                            onChange={event => choose(slot, event.target.value)}
                            options={
                              overviewLoading
                                ? [{ value: "", label: PICKER_LOADING_LABEL }]
                                : [
                                    { value: "", label: "Choose a practice area" },
                                    ...candidates
                                      .filter(
                                        item =>
                                          item.editionBpaId !==
                                          cards.find((_, other) => other !== slot)?.editionBpaId,
                                      )
                                      .map(item => ({
                                        value: item.editionBpaId,
                                        label: item.name,
                                      })),
                                  ]
                            }
                          />
                        </div>
                      )}
                      {card.moveInVisible && moveInReason === null && card.bpaId && (
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          className={shared.cardButton}
                          onClick={() => openMoveIn(card.bpaId)}
                        >
                          Move in
                        </Button>
                      )}
                      {card.moveInVisible && moveInReason !== null && (
                        <Button
                          variant="outlined"
                          color="default"
                          size="small"
                          className={shared.cardButton}
                          disabled
                          title={moveInReason}
                        >
                          Move in
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {moveInReason && cards.some(card => card.moveInVisible) && (
              <p className={hub.reason}>{moveInReason}</p>
            )}
            {overviewQuery.isError && (
              <div className={styles.inlineError} role="alert">
                <span>
                  Couldn't check which practice areas are empty: {messageOf(overviewQuery.error)}
                </span>
                <Button
                  variant="outlined"
                  color="default"
                  size="small"
                  onClick={() => overviewQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            )}
          </section>

          <section className={hub.box} aria-labelledby="split-still-in-heading">
            <div className={hub.boxHeader}>
              <h2 id="split-still-in-heading" className={hub.micro}>
                Still in the original
              </h2>
              {stillInKnown && <span className={hub.count}>{rows.length}</span>}
            </div>
            {summaryFailed && (
              <div className={hub.error} role="alert">
                <span>{messageOf(summaryQuery.error)}</span>
                <Button variant="outlined" color="default" onClick={() => summaryQuery.refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {!stillInKnown && (
              <p className={hub.muted}>
                {stillInUnavailableText({
                  editionStatus,
                  originalArchived,
                  originalName,
                })}
              </p>
            )}
            {stillInKnown && view.emptyText && <p className={hub.muted}>{view.emptyText}</p>}
            {stillInKnown && !view.emptyText && (
              <table className={hub.table}>
                <thead>
                  <tr>
                    <th scope="col">Organisation</th>
                    <th scope="col" className={hub.number}>
                      Submissions
                    </th>
                    <th scope="col" className={hub.number}>
                      Rankings
                    </th>
                    <th scope="col" className={hub.number}>
                      Commentary
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {view.rows.map(row => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td className={hub.number}>{row.counts.submissions}</td>
                      <td className={hub.number}>{row.counts.rankings}</td>
                      <td className={hub.number}>{row.counts.commentary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {view.showAllLabel && (
              <Button variant="link" className={styles.showAll} onClick={() => setShowAll(true)}>
                {view.showAllLabel}
              </Button>
            )}
          </section>

          {archiveError && (
            <div className={hub.error} role="alert">
              <span>{archiveError}</span>
            </div>
          )}
        </div>

        <div className={hub.footer}>
          <span className={hub.progress}>{model.progress}</span>
          <span className={hub.footerActions}>
            {archiveReason && archiveOffered && (
              <span id={ARCHIVE_REASON_ID} className={hub.reason}>
                {archiveReason}
              </span>
            )}
            {!model.archiveIsMain && archiveButton}
            <Button
              className={shared.compactButton}
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.research.cmsBpa(editionId, bpaId))}
            >
              Close
            </Button>
            {model.archiveIsMain && archiveButton}
          </span>
        </div>
      </div>

      {confirming && (
        <CmsConfirmDialog
          title={`Archive ${originalName}`}
          message={warning}
          detail={
            details.length > 0 ? (
              <ul className={hub.confirmList}>
                {details.map(line => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : undefined
          }
          confirmLabel="Archive"
          cancelLabel="Cancel"
          tone="warning"
          returnFocusTo={archiveButtonRef}
          onConfirm={archiveOriginal}
          onCancel={() => setConfirming(false)}
        />
      )}
    </CmsLayout>
  );
}
